import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { 
  getWorkspaceMembers, 
  deleteWorkspace, 
  getWorkspaceStats,
  removeWorkspaceMember,
  updateMemberRole,
  getProjects,
  updateProjectPermission,
  getProjectCollaborators
} from '../../services/api';
import { toast } from 'react-toastify';
import WorkspaceModal from '../WorkspaceModal';
import AddMemberModal from './AddMemberModal';
import APIKeysTab from './APIKeysTab';
import { useModal } from '../../contexts/ModalContext';
import CustomSelect from '../ui/CustomSelect';

const WorkspaceSettings = ({ onBack }) => {
  const location = useLocation();
  const { currentWorkspace, removeWorkspace, loading: workspaceLoading, loadWorkspaces } = useWorkspace();
  const { showConfirm } = useModal();
  const [members, setMembers] = useState([]);
  const [stats, setStats] = useState(null);
  const [, setDataLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    // Check if we should open a specific tab from navigation state
    return location.state?.tab ?? 'general';
  });
  const [projects, setProjects] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [projectPermissions, setProjectPermissions] = useState({});

  const loadWorkspaceData = useCallback(async () => {
    if (!currentWorkspace) return;
    
    setDataLoading(true);
    
    const workspaceId = currentWorkspace.id || currentWorkspace._id;
    
    const [membersResult, statsResult] = await Promise.all([
      getWorkspaceMembers(workspaceId),
      getWorkspaceStats(workspaceId)
    ]);

    if (membersResult.success) {
      setMembers(membersResult.data || []);
    }

    if (statsResult.success) {
      setStats(statsResult.data);
    }

    setDataLoading(false);
  }, [currentWorkspace]);

  useEffect(() => {
    if (currentWorkspace) {
      loadWorkspaceData();
    }
  }, [currentWorkspace, loadWorkspaceData]);

  useEffect(() => {
    const loadPermissionsData = async () => {
      setPermissionsLoading(true);
      const workspaceId = currentWorkspace.id || currentWorkspace._id;
      
      const projectsResult = await getProjects(100, 0, workspaceId);
      if (projectsResult.success) {
        setProjects(projectsResult.data || []);
        
        const perms = {};
        for (const project of projectsResult.data || []) {
          const projectId = project.id || project._id;
          const collabResult = await getProjectCollaborators(projectId);
          if (collabResult.success) {
            perms[projectId] = {};
            collabResult.data.collaborators.forEach(collab => {
              perms[projectId][collab.user_id] = collab.permission;
            });
          }
        }
        setProjectPermissions(perms);
      }
      
      setPermissionsLoading(false);
    };

    if (currentWorkspace && activeTab === 'permissions') {
      loadPermissionsData();
    }
  }, [currentWorkspace, activeTab]);

  const handleUpdatePermission = async (projectId, userId, permission) => {
    const workspaceId = currentWorkspace.id || currentWorkspace._id;
    const result = await updateProjectPermission(workspaceId, projectId, userId, permission);
    
    if (result.success) {
      if (permission === 'none') {
        toast.success('Access removed');
        setProjectPermissions(prev => {
          const newPerms = { ...prev };
          if (newPerms[projectId]) {
            delete newPerms[projectId][userId];
          }
          return newPerms;
        });
      } else {
        toast.success(`Permission updated to ${permission}`);
        setProjectPermissions(prev => ({
          ...prev,
          [projectId]: {
            ...prev[projectId],
            [userId]: permission
          }
        }));
      }
    } else {
      toast.error(result.error || 'Failed to update permission');
    }
  };

  const handleDeleteWorkspace = async () => {
    const confirmed = await showConfirm({
      title: 'Delete Workspace',
      message: `Are you sure you want to delete "${currentWorkspace.name}"? This action cannot be undone.`,
      confirmText: 'Delete Workspace',
      variant: 'danger'
    });
    if (!confirmed) return;

    const workspaceId = currentWorkspace.id || currentWorkspace._id;
    const result = await deleteWorkspace(workspaceId);
    
    if (result.success) {
      toast.success('Workspace deleted successfully');
      removeWorkspace(workspaceId);
      if (onBack) onBack();
    } else {
      toast.error(result.error || 'Failed to delete workspace');
    }
  };

  const handleRemoveMember = async (userId) => {
    const confirmed = await showConfirm({
      title: 'Remove Member',
      message: 'Are you sure you want to remove this member?',
      confirmText: 'Remove',
      variant: 'danger'
    });
    if (!confirmed) return;

    const workspaceId = currentWorkspace.id || currentWorkspace._id;
    const result = await removeWorkspaceMember(workspaceId, userId);
    
    if (result.success) {
      toast.success('Member removed successfully');
      loadWorkspaceData();
    } else {
      toast.error(result.error || 'Failed to remove member');
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    const workspaceId = currentWorkspace.id || currentWorkspace._id;
    const result = await updateMemberRole(workspaceId, userId, newRole);
    
    if (result.success) {
      toast.success('Role updated successfully');
      loadWorkspaceData();
    } else {
      toast.error(result.error || 'Failed to update role');
    }
  };

  const handleMemberAdded = () => {
    loadWorkspaceData();
  };

  const handleEditModalClose = () => {
    setShowEditModal(false);
    loadWorkspaces();
  };

  if (workspaceLoading || !currentWorkspace) {
    return (
      <div className="flex-1 flex items-center justify-center bg-bg-primary">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-border-primary border-t-accent-primary rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text-secondary">Loading workspace...</p>
        </div>
      </div>
    );
  }

  const isOwner = currentWorkspace.user_role === 'owner';
  const isAdmin = currentWorkspace.user_role === 'admin' || isOwner;

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-bg-primary">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-border-primary bg-bg-secondary px-6 py-4">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-text-secondary hover:text-text-primary bg-transparent border border-border-primary rounded-lg hover:bg-bg-hover hover:border-border-hover transition-all duration-base"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="19" y1="12" x2="5" y2="12"></line>
                <polyline points="12 19 5 12 12 5"></polyline>
              </svg>
              Back
            </button>
            <div>
              <h1 className="text-2xl font-semibold text-text-primary tracking-tight">Workspace Settings</h1>
              <p className="text-sm text-text-tertiary mt-0.5">{currentWorkspace.name}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex-shrink-0 border-b border-border-primary bg-bg-secondary px-6">
        <div className="flex gap-1 max-w-7xl mx-auto">
          <button
            onClick={() => setActiveTab('general')}
            className={`px-4 py-3 text-sm font-semibold border-b-2 transition-all duration-base ${
              activeTab === 'general'
                ? 'border-primary-500 text-primary-500'
                : 'border-transparent text-text-secondary hover:text-text-primary'
            }`}
          >
            General
          </button>
          <button
            onClick={() => setActiveTab('members')}
            className={`px-4 py-3 text-sm font-semibold border-b-2 transition-all duration-base ${
              activeTab === 'members'
                ? 'border-primary-500 text-primary-500'
                : 'border-transparent text-text-secondary hover:text-text-primary'
            }`}
          >
            Members
            <span className="ml-2 inline-flex items-center justify-center min-w-5 h-5 px-2 text-xs font-semibold text-primary-500 bg-primary-50 rounded-full">
              {members.length}
            </span>
          </button>
          {isAdmin && (
            <button
              onClick={() => setActiveTab('permissions')}
              className={`px-4 py-3 text-sm font-semibold border-b-2 transition-all duration-base ${
                activeTab === 'permissions'
                  ? 'border-primary-500 text-primary-500'
                  : 'border-transparent text-text-secondary hover:text-text-primary'
              }`}
            >
              Project Access
          </button>
          )}
          {isAdmin && (
            <button
              onClick={() => setActiveTab('apikeys')}
              className={`px-4 py-3 text-sm font-semibold border-b-2 transition-all duration-base ${
                activeTab === 'apikeys'
                  ? 'border-primary-500 text-primary-500'
                  : 'border-transparent text-text-secondary hover:text-text-primary'
              }`}
            >
              API Keys
            </button>
          )}
          {isOwner && (
            <button
              onClick={() => setActiveTab('danger')}
              className={`px-4 py-3 text-sm font-semibold border-b-2 transition-all duration-base ${
                activeTab === 'danger'
                  ? 'border-error text-error'
                  : 'border-transparent text-text-secondary hover:text-error'
              }`}
            >
              Danger Zone
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-5xl mx-auto">
          {activeTab === 'general' && (
            <div className="space-y-6">
              {/* Workspace Info */}
              <div className="bg-bg-secondary border border-border-primary rounded-lg overflow-hidden shadow-sm">
                <div className="px-6 py-4 border-b border-border-primary bg-gradient-to-b from-bg-secondary to-bg-tertiary">
                  <h3 className="text-lg font-semibold text-text-primary">Workspace Information</h3>
                </div>
                <div className="p-6 space-y-5">
                  <div className="grid grid-cols-2 gap-4 pb-5 border-b border-border-primary">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-primary-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                        </svg>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-text-primary">{stats?.project_count || 0}</div>
                        <div className="text-xs text-text-tertiary font-medium">Projects</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-success-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-success-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                          <circle cx="9" cy="7" r="4"/>
                          <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                          <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                        </svg>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-text-primary">{stats?.member_count || members.length}</div>
                        <div className="text-xs text-text-tertiary font-medium">Members</div>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">Name</label>
                    <div className="text-base font-medium text-text-primary">{currentWorkspace.name}</div>
                      {isAdmin && (
                        <button
                          onClick={() => setShowEditModal(true)}
                        className="mt-2 text-sm text-primary-500 hover:text-primary-600 underline transition-colors"
                        >
                        Edit Name
                        </button>
                      )}
                    </div>

                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">Slug</label>
                    <div className="text-base font-mono text-text-primary">{currentWorkspace.slug}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">Your Role</label>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-primary-50 text-primary-600 capitalize">
                      {currentWorkspace.user_role}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'members' && (
            <div className="bg-bg-secondary border border-border-primary rounded-lg shadow-sm">
              <div className="px-6 py-4 border-b border-border-primary bg-gradient-to-b from-bg-secondary to-bg-tertiary flex items-center justify-between rounded-t-lg">
                <div>
                <h3 className="text-lg font-semibold text-text-primary">Team Members</h3>
                  <p className="text-sm text-text-tertiary mt-0.5">Manage who has access to this workspace</p>
              </div>
                {isAdmin && (
                  <button 
                    onClick={() => setShowAddMemberModal(true)} 
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-500 hover:shadow-lg transition-all duration-base"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="5" x2="12" y2="19"></line>
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                    Add Member
                  </button>
                )}
                      </div>
                      <div>
                {members.map((member) => (
                  <div 
                    key={member.user_id} 
                    className="flex items-center justify-between px-6 py-4 border-b border-border-primary last:border-b-0 hover:bg-bg-hover transition-colors duration-base"
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm shadow-sm flex-shrink-0 bg-gradient-to-br from-primary-600 to-primary-500 text-black shadow-glow-sm">
                        {(member.username || member.email || 'U')[0].toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-text-primary truncate">{member.username || member.email}</div>
                        <div className="text-xs text-text-tertiary capitalize">{member.role}</div>
                      </div>
                    </div>
                    {isAdmin && member.role !== 'owner' && (
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <CustomSelect
                          value={member.role}
                          onChange={(e) => handleUpdateRole(member.user_id, e.target.value)}
                          options={[
                            { value: 'member', label: 'Member' },
                            { value: 'admin', label: 'Admin' }
                          ]}
                          size="default"
                        />
                        <button
                          onClick={() => handleRemoveMember(member.user_id)}
                          className="p-2 text-text-tertiary hover:text-error hover:bg-error-bg rounded-lg transition-all duration-base"
                          title="Remove member"
                        >
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'permissions' && isAdmin && (
            <div className="bg-bg-secondary border border-border-primary rounded-lg shadow-sm">
              <div className="px-6 py-4 border-b border-border-primary bg-gradient-to-b from-bg-secondary to-bg-tertiary">
                <h3 className="text-lg font-semibold text-text-primary">Project Access Control</h3>
                <p className="text-sm text-text-tertiary mt-1">
                  Manage who can access each project and their permission level
                </p>
              </div>

              {permissionsLoading ? (
                <div className="p-12 text-center">
                  <div className="w-12 h-12 border-4 border-border-primary border-t-accent-primary rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-text-secondary">Loading permissions...</p>
                </div>
              ) : projects.length === 0 ? (
                <div className="p-12 text-center">
                  <svg className="w-16 h-16 text-text-tertiary mx-auto mb-4 opacity-50" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                  </svg>
                  <p className="text-text-tertiary">No prompts yet</p>
                </div>
              ) : (
                <div className="overflow-visible">
                  <table className="w-full overflow-visible">
                    <thead className="bg-bg-tertiary border-b border-border-primary">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider sticky left-0 bg-bg-tertiary z-10">
                          Project
                        </th>
                        {members.map(member => (
                          <th key={member.user_id} className="px-4 py-3 text-center text-xs font-semibold text-text-secondary uppercase tracking-wider min-w-32">
                            <div className="flex flex-col items-center gap-1">
                              <div className="w-8 h-8 rounded-full bg-accent-primary/20 text-accent-primary flex items-center justify-center text-sm font-semibold">
                                {(member.full_name || member.username || 'U').charAt(0).toUpperCase()}
                              </div>
                              <div className="text-xs font-normal text-text-tertiary truncate max-w-24">
                                {member.username}
                              </div>
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border-primary overflow-visible">
                      {projects.map(project => {
                        const projectId = project.id || project._id;
                        const projectName = project.project_name || project.name || 'Untitled';
                        
                        return (
                          <tr key={projectId} className="hover:bg-bg-hover transition-colors overflow-visible">
                            <td className="px-6 py-4 sticky left-0 bg-bg-secondary z-10">
                              <div className="font-medium text-text-primary text-sm">{projectName}</div>
                              {project.description && (
                                <div className="text-xs text-text-tertiary truncate max-w-xs mt-0.5">
                                  {project.description}
                                </div>
                              )}
                            </td>
                            {members.map(member => {
                              const isProjectOwner = project.user_id === member.user_id;
                              const currentPerm = projectPermissions[projectId]?.[member.user_id] || 'none';
                              const displayPerm = isProjectOwner ? 'owner' : currentPerm;
                              
                              return (
                                <td key={member.user_id} className="px-4 py-4 overflow-visible">
                                  <div className="flex justify-center relative">
                                    {isProjectOwner ? (
                                      <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded bg-primary-500/15 text-primary-500 border border-primary-500/30">
                                        Owner
                                      </span>
                                    ) : (
                                      <CustomSelect
                                        value={displayPerm}
                                        onChange={(e) => handleUpdatePermission(projectId, member.user_id, e.target.value)}
                                        options={[
                                          { value: 'none', label: 'No Access' },
                                          { value: 'view', label: 'View Only' },
                                          { value: 'edit', label: 'Can Edit' }
                                        ]}
                                        size="small"
                                      />
                                    )}
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'apikeys' && isAdmin && (
            <APIKeysTab workspaceId={currentWorkspace.id || currentWorkspace._id} />
          )}

          {activeTab === 'danger' && isOwner && (
            <div className="bg-error-bg border border-error/30 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-error mb-2">Delete Workspace</h3>
              {currentWorkspace.is_default ? (
                <div>
                  <p className="text-sm text-text-secondary mb-2">
                    This is your default workspace and cannot be deleted.
                  </p>
                  <div className="flex items-center gap-2 text-sm text-warning-400 bg-warning-bg border border-warning/30 rounded-lg p-3 mt-3">
                    <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                      <line x1="12" y1="9" x2="12" y2="13"></line>
                      <line x1="12" y1="17" x2="12.01" y2="17"></line>
                    </svg>
                    <span>Default workspace is protected and cannot be removed</span>
                  </div>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-text-secondary mb-4">
                    Once you delete a workspace, there is no going back. All projects, runs, and data will be permanently deleted.
                  </p>
                  <button
                    onClick={handleDeleteWorkspace}
                    className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-error rounded-lg hover:bg-error/90 hover:shadow-lg transition-all duration-base"
                  >
                    Delete Workspace
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showEditModal && (
        <WorkspaceModal
          isOpen={showEditModal}
          onClose={handleEditModalClose}
          workspace={currentWorkspace}
        />
      )}

      {showAddMemberModal && (
        <AddMemberModal
          isOpen={showAddMemberModal}
          onClose={() => setShowAddMemberModal(false)}
          workspaceId={currentWorkspace.id || currentWorkspace._id}
          onMemberAdded={handleMemberAdded}
        />
      )}
    </div>
  );
};

WorkspaceSettings.propTypes = {
  onBack: PropTypes.func,
};

WorkspaceSettings.defaultProps = {
  onBack: () => {},
};

export default WorkspaceSettings;
